import React, { useState, useMemo, useCallback } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { 
  Box, 
  IconButton, 
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon
} from '@mui/icons-material';
import { formatDate } from '../../helpers/UtilsFunctions';
import { dataAPI } from '../../helpers/api_service';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import TrashIcon from '../../assets/Trash.svg';
import { DataTable, AlertDialog } from '../Common';

// Helper function to handle profile picture URL
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

const EmployeeList = ({ 
  employees, 
  onDeleteEmployee, 
  onEditEmployee,
  onViewEmployee,
  searchTerm: externalSearchTerm, 
  onSearchChange: externalOnSearchChange,
  showSearchOnly = false 
}) => {
  const { mode } = useThemeMode();
  const { hasEditPermission, hasDeletePermission } = usePermissions();
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    employeeId: null,
    employeeName: ''
  });

  // Use external search term if provided, otherwise use internal state
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = externalOnSearchChange || setInternalSearchTerm;

  // Search function for employees
  const searchEmployees = (employees, searchTerm) => {
    if (!searchTerm) return employees;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return employees.filter(employee => 
      employee.username?.toLowerCase().includes(lowerSearchTerm) ||
      employee.full_name?.toLowerCase().includes(lowerSearchTerm) ||
      employee.email?.toLowerCase().includes(lowerSearchTerm) ||
      employee.role_name?.toLowerCase().includes(lowerSearchTerm)
    );
  };

  // Delete handlers
  const handleDeleteClick = useCallback((employee) => {
    setDeleteDialog({
      isOpen: true,
      employeeId: employee.id,
      employeeName: employee.full_name || employee.username
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.employeeId) return;

    try {
      await dataAPI.deleteEmployee(deleteDialog.employeeId);
      
      // Call the parent's delete handler to update the UI
      if (onDeleteEmployee) {
        onDeleteEmployee(deleteDialog.employeeId);
      }
      setDeleteDialog({ isOpen: false, employeeId: null, employeeName: '' });
    } catch (error) {
      console.error('Error deleting employee:', error);
      setDeleteDialog({ isOpen: false, employeeId: null, employeeName: '' });
    }
  }, [deleteDialog.employeeId, onDeleteEmployee]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialog({ isOpen: false, employeeId: null, employeeName: '' });
  }, []);

  // Function to format role name from camelCase to proper case
  const formatRoleName = (roleName) => {
    if (!roleName) return 'N/A';
    
    // Add space before capital letters and capitalize first letter
    return roleName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim(); // Remove leading/trailing spaces
  };

  // Memoize columns to prevent recreation on every render
  const columns = useMemo(() => [
    {
      accessorKey: 'full_name',
      header: 'Employee Name',
      size: 200,
      minSize: 180,
      maxSize: 280,
      Cell: ({ row }) => {
        const profilePicUrl = getProfilePicUrl(row.original.profile_pic);
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px' 
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              overflow: 'hidden',
              backgroundColor: mode === 'dark' ? '#444' : '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {profilePicUrl ? (
                <img 
                  src={profilePicUrl} 
                  alt="Profile" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div style={{
                width: '100%',
                height: '100%',
                backgroundColor: mode === 'dark' ? '#444' : '#f0f0f0',
                display: profilePicUrl ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: mode === 'dark' ? '#999' : '#666',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {row.original.full_name ? row.original.full_name.charAt(0).toUpperCase() : 'U'}
              </div>
            </div>
            <span style={{ 
              color: mode === 'dark' ? '#FFFFFF' : '#333333',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              {row.original.full_name || 'N/A'}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'role_name',
      header: 'Role',
      size: 120,
      minSize: 100,
      maxSize: 150,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {formatRoleName(row.original.role_name)}
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      size: 200,
      minSize: 180,
      maxSize: 280,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.email}
        </span>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      size: 100,
      minSize: 80,
      maxSize: 120,
      Cell: ({ row }) => {
        const isActive = row.original.is_active;
        const color = isActive ? '#4caf50' : '#f44336'; // Green for active, red for inactive
        const status = isActive ? 'Active' : 'Inactive';
        
        return (
          <span style={{ 
            color: color,
            fontSize: '14px',
            fontFamily: 'Urbanist, Arial, sans-serif',
            fontWeight: '500',
            wordWrap: 'break-word',
            whiteSpace: 'normal'
          }}>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Added Date',
      size: 100,
      minSize: 90,
      maxSize: 110,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 80,
      minSize: 70,
      maxSize: 90,
      enableSorting: false,
      Cell: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasEditPermission('employee') && (
              <Tooltip title="Edit">
                <IconButton 
                  size="small" 
                  sx={{ color: '#26C3C0' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEditEmployee && onEditEmployee(row.original);
                  }}
                >
                  <EditIcon fontSize="small" style={{ width: '20px', height: '20px', color: '#26C3C0' }} />
                </IconButton>
              </Tooltip>
            )}
            {hasDeletePermission('employee') && (
              <Tooltip title="Delete">
                <IconButton 
                  size="small" 
                  sx={{ color: '#f44336' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteClick(row.original);
                  }}
                >
                  <img src={TrashIcon} alt="Delete" style={{ width: '20px', height: '20px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [mode, onDeleteEmployee, onEditEmployee, handleDeleteClick, hasEditPermission, hasDeletePermission]);

  return (
    <div className={showSearchOnly ? '' : 'employees-list-wrapper'}>
      <DataTable
        data={employees}
        columns={columns}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showSearchOnly={showSearchOnly}
        searchPlaceholder="Search employees..."
        searchFunction={searchEmployees}
        emptyMessage="No employees found"
        enableSorting={true}
        enableRowSelection={false}
        onRowClick={showSearchOnly ? null : onViewEmployee}
        shadow={false}
        borderRadius="0"
        showHorizontalScroll={!showSearchOnly}
        customStyles={{
          boxShadow: 'none',
          borderRadius: '0',
          '& .MuiTableRow-root': {
            borderRadius: '0 !important',
          },
          '& .MuiTableCell-root': {
            borderRadius: '0 !important',
          },
          '& .MuiTableRow-root:last-child': {
            borderRadius: '0 !important',
            '& .MuiTableCell-root': {
              borderRadius: '0 !important',
            }
          }
        }}
      />
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        type="delete"
        title="Delete Employee"
        message="Are you sure you want to delete"
        itemName={deleteDialog.employeeName}
        confirmText="Delete"
        icon={TrashIcon}
      />
    </div>
  );
};

export default EmployeeList;